import { Input } from '@/components/ui/input';

/**
 * Text input constrained to a valid decimal money string (e.g. "1234.56").
 *
 * Money is stored and transmitted as decimal strings throughout the AR stack,
 * so this emits a string rather than a number — avoiding float rounding. Emits
 * `null` when the field is cleared.
 */
export function DecimalInput({
  value,
  onChange,
  placeholder = '0.00',
  id,
  disabled,
  className,
  maxFractionDigits = 2,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
  maxFractionDigits?: number;
}) {
  function handleChange(raw: string) {
    const trimmed = raw.trim();
    if (trimmed === '') {
      onChange(null);
      return;
    }
    // Allow an optional leading minus, digits, and a single decimal point with
    // up to `maxFractionDigits` fractional digits. Reject anything else so the
    // value stays a parseable decimal string. Clamp the bound defensively so a
    // bad prop (NaN, negative) can't produce an invalid quantifier that throws.
    const fractionDigits =
      Number.isInteger(maxFractionDigits) && maxFractionDigits >= 0
        ? maxFractionDigits
        : 2;
    const pattern = new RegExp(`^-?\\d*(\\.\\d{0,${fractionDigits}})?$`);
    if (!pattern.test(trimmed)) return;
    onChange(trimmed);
  }

  return (
    <Input
      id={id}
      inputMode="decimal"
      value={value ?? ''}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}
